import type {Content, KeyTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import cn from 'classnames'
import {useSearchParams} from 'next/navigation'
import type {FieldValues} from 'react-hook-form'
import {FormProvider, useForm} from 'react-hook-form'
import {Button} from 'src/common/Button'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {
  appendCurrentParams,
  useGetOriginPageParam,
} from 'src/common/hooks/useGetPricingFormHandler/useGetPricingFormHandler'
import type {Context} from 'src/common/types'
import {RadioButtonField} from './components/RadioButton'

/**
 * Props for `ProductSelectorSection`.
 */
export type ProductSelectorSectionProps = SliceComponentProps<Content.ProductSelectorSectionSlice>

/**
 * Component for "ProductSelectorSection" Slices.
 */
const ProductSelectorSection = ({
  slice: {primary, slice_label, slice_type, variation},
  index,
  context: {pageUid},
}: ProductSelectorSectionProps & {context: Context}): JSX.Element => {
  const sanitizedTitle = sanitizeHtml(primary.title || '')
  const defaultOption = primary.products[0]?.parameter_value || ''
  const successUrl = isFilled.link(primary.submit_button_link) ? primary.submit_button_link.url : ''
  const params = new URLSearchParams()
  const currentParams = useSearchParams()
  const originPage = useGetOriginPageParam()

  const formHandler = (data: FieldValues) => {
    const selectedProduct = data.product || ''

    params.append(selectedProduct as string, 'true')

    appendCurrentParams(params, currentParams)

    params.append('origin-page', originPage)
    params.append('element', slice_type)
    params.append('intent', 'self_serve')

    return (location.href = `${successUrl}?${params.toString()}`)
  }

  const methods = useForm({
    defaultValues: {
      product: defaultOption,
    },
  })

  const {handleSubmit} = methods
  const onSubmit = handleSubmit(formHandler)

  return (
    <Container
      data-slice-type={slice_type}
      data-slice-variation={variation}
      data-slice-label={slice_label}
      backgroundColor="Moss White"
      className={cn('md:max-lg:px-10', {
        'lg:!pb-0 max-md:px-0': primary.enable_reskin,
        'max-md:px-5': !primary.enable_reskin,
      })}
    >
      <div
        className={cn(
          'mx-auto flex w-full max-w-[1200px] flex-col items-center bg-beige-100 px-6 py-[30px] md:px-10 md:pb-5 lg:px-[90px]',
          {
            'bg-beige-100 rounded-xl md:rounded-2xl md:gap-6 gap-3': !primary.enable_reskin,
            'bg-moss-grey-100 max-md:rounded-none md:rounded-lg md:gap-12 gap-6': primary.enable_reskin,
          },
        )}
      >
        <div
          className={cn('flex flex-col gap-1 text-center', {
            'gap-1 max-md:pb-2': !primary.enable_reskin,
            'gap-6': primary.enable_reskin,
          })}
        >
          <h2
            id="section-title"
            className="text-h3 text-center font-medium tracking-[-.32px] text-moss-black"
            dangerouslySetInnerHTML={{__html: sanitizedTitle}}
          />
          <p
            className={cn('text-body-s text-grey-550', {
              'text-body-s': !primary.enable_reskin,
              'text-tg-body-m': primary.enable_reskin,
            })}
          >
            {primary.subtitle}
          </p>
        </div>
        <FormProvider {...methods}>
          <form
            onSubmit={onSubmit}
            data-hs-do-not-collect
            className={cn('flex w-full flex-col', {
              'gap-6 md:gap-12': primary.enable_reskin,
              'gap-3 md:gap-6': !primary.enable_reskin,
            })}
          >
            <fieldset aria-labelledby="section-title" className="w-full">
              <div
                className={cn('flex w-full flex-col gap-2 md:flex-row', {
                  'gap-2 md:gap-3': !primary.enable_reskin,
                  'gap-6 md:gap-3': primary.enable_reskin,
                })}
              >
                {isFilled.group(primary.products) &&
                  primary.products.map((product) => (
                    <RadioButtonField
                      key={product.product_name}
                      product={product}
                      enableReskin={primary.enable_reskin}
                    />
                  ))}
              </div>
            </fieldset>
            <div className="flex w-full flex-col gap-2">
              {isFilled.link(primary.submit_button_link) && (
                <VariationButton
                  enableReskin={primary.enable_reskin}
                  index={index}
                  pageUid={pageUid}
                  label={primary.submit_button_text}
                />
              )}
              {isFilled.keyText(primary.disclaimer) && (
                <p className="text-12 font-normal leading-4 tracking-[.24px] text-grey-300">{primary.disclaimer}</p>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </Container>
  )
}

export default ProductSelectorSection

const VariationButton = ({
  enableReskin,
  index,
  pageUid,
  label,
}: {
  enableReskin: boolean
  index: number
  pageUid: string
  label: KeyTextField
}) => {
  if (enableReskin) {
    return (
      <ButtonV2
        variant="primary"
        className="h-[50px] w-full max-w-[400px] self-center"
        type="submit"
        trackingData={{
          mktgId: 'product-selector-button',
          location: `component-${index}-product-selector-section`,
          pageName: pageUid,
          dataType: 'freemium-button',
        }}
      >
        {label}
      </ButtonV2>
    )
  } else {
    return (
      <Button
        variant="fillV2"
        className="h-[50px] w-full max-w-[400px] self-center"
        type="submit"
        data-mktg-id="product-selector-button"
        data-page-location={`component-${index}-product-selector-section`}
        data-page-name={pageUid}
        data-type="freemium-button"
      >
        {label}
      </Button>
    )
  }
}
